// src/pages/Authentication/UserProfile/PersonalInfoCard.tsx
// Editable personal information card with Formik

import React from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  Button,
  Form,
  FormFeedback,
  Spinner,
} from 'reactstrap';
import { useFormik } from 'formik';
import { personalInfoSchema } from './validationSchema';
import { PersonalInfoFormValues } from './types';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('authentication');

interface PersonalInfoCardProps {
  email: string;
  initialValues: PersonalInfoFormValues;
  loading: boolean;
  onSubmit: (values: PersonalInfoFormValues) => void;
}

const PersonalInfoCard: React.FC<PersonalInfoCardProps> = ({
  email,
  initialValues,
  loading,
  onSubmit,
}) => {
  const validation = useFormik<PersonalInfoFormValues>({
    enableReinitialize: true,
    initialValues,
    validationSchema: personalInfoSchema,
    onSubmit,
  });

  return (
    <Card className="mb-3">
      <CardHeader className="d-flex align-items-center justify-content-between">
        <h5 className="card-title mb-0">
          <i className="ri-user-line me-2"></i>
          {st('thong_tin_ca_nhan_title')}
        </h5>
        <Button
          color="primary"
          onClick={() => validation.handleSubmit()}
          disabled={loading}
        >
          {loading ? (
            <>
              <Spinner size="sm" className="me-2" />
              {st('dang_luu_text')}
            </>
          ) : (
            <>
              <i className="ri-save-line me-1"></i>
              {st('luu_thay_doi_text')}
            </>
          )}
        </Button>
      </CardHeader>

      <CardBody>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            validation.handleSubmit();
            return false;
          }}
        >
          <Row>
            {/* Họ và Tên */}
            <Col md={6}>
              <FormGroup>
                <Label className="form-label">
                  {st('ho_va_ten_label')} <span className="text-danger">*</span>
                </Label>
                <Input
                  type="text"
                  name="fullName"
                  placeholder={st('nhap_ho_va_ten_placeholder')}
                  value={validation.values.fullName}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  invalid={
                    validation.touched.fullName && !!validation.errors.fullName
                  }
                />
                {validation.touched.fullName && validation.errors.fullName && (
                  <FormFeedback>{validation.errors.fullName}</FormFeedback>
                )}
              </FormGroup>
            </Col>

            {/* Email (disabled) */}
            <Col md={6}>
              <FormGroup>
                <Label className="form-label">{st('email_dang_nhap_label')}</Label>
                <Input type="email" value={email} disabled className="bg-light" />
                <small className="text-muted">{st('email_khong_the_thay_doi_hint')}</small>
              </FormGroup>
            </Col>

            {/* Số điện thoại */}
            <Col md={6}>
              <FormGroup>
                <Label className="form-label">
                  {st('so_dien_thoai_label')} <span className="text-danger">*</span>
                </Label>
                <Input
                  type="text"
                  name="phone"
                  placeholder={st('vd_0912345678_placeholder')}
                  maxLength={10}
                  value={validation.values.phone}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  invalid={
                    validation.touched.phone && !!validation.errors.phone
                  }
                />
                {validation.touched.phone && validation.errors.phone && (
                  <FormFeedback>{validation.errors.phone}</FormFeedback>
                )}
              </FormGroup>
            </Col>

            {/* CMND/CCCD */}
            <Col md={6}>
              <FormGroup>
                <Label className="form-label">
                  {st('cmnd_cccd_label')} <span className="text-danger">*</span>
                </Label>
                <Input
                  type="text"
                  name="idCard"
                  placeholder={st('vd_001234567890_placeholder')}
                  maxLength={12}
                  value={validation.values.idCard}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  invalid={
                    validation.touched.idCard && !!validation.errors.idCard
                  }
                />
                {validation.touched.idCard && validation.errors.idCard && (
                  <FormFeedback>{validation.errors.idCard}</FormFeedback>
                )}
              </FormGroup>
            </Col>

            {/* Ngày sinh */}
            <Col md={6}>
              <FormGroup>
                <Label className="form-label">
                  {st('ngay_sinh_label')} <span className="text-danger">*</span>
                </Label>
                <Input
                  type="date"
                  name="dateOfBirth"
                  value={validation.values.dateOfBirth}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  invalid={
                    validation.touched.dateOfBirth &&
                    !!validation.errors.dateOfBirth
                  }
                />
                {validation.touched.dateOfBirth &&
                  validation.errors.dateOfBirth && (
                    <FormFeedback>{validation.errors.dateOfBirth}</FormFeedback>
                  )}
              </FormGroup>
            </Col>

            {/* Địa chỉ */}
            <Col md={6}>
              <FormGroup>
                <Label className="form-label">
                  {st('dia_chi_thuong_tru_label')} <span className="text-danger">*</span>
                </Label>
                <Input
                  type="text"
                  name="address"
                  placeholder={st('nhap_dia_chi_thuong_tru_placeholder')}
                  value={validation.values.address}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  invalid={
                    validation.touched.address && !!validation.errors.address
                  }
                />
                {validation.touched.address && validation.errors.address && (
                  <FormFeedback>{validation.errors.address}</FormFeedback>
                )}
              </FormGroup>
            </Col>
          </Row>
        </Form>
      </CardBody>
    </Card>
  );
};

export default PersonalInfoCard;